import { computed, watch } from 'vue';
import { defineStore } from 'pinia';
import { clamp } from 'lodash';
import { useHipSpineStore } from '@/views/3d-planner/hipSpine/hipSpineStore';
import { useHipSpineXRaySceneViewModel } from '@/views/3d-planner/hipSpine/useHipSpineXRaySceneViewModel';
import { fullViewCameraParams } from '@/views/dicoms/xrays/xraySceneState';

/** Additive brightness offset applied in the X-ray shader. */
export const BRIGHTNESS_MIN = -0.5;
export const BRIGHTNESS_MAX = 0.5;
export const BRIGHTNESS_DEFAULT = 0;

/** Multiplicative contrast around mid-gray applied in the X-ray shader. */
export const CONTRAST_MIN = 0.5;
export const CONTRAST_MAX = 2;
export const CONTRAST_DEFAULT = 1;

/** Zoom slider band, expressed as a multiple of the full-image fit (1 = fit). */
export const ZOOM_MIN = 1;
export const ZOOM_MAX = 4;

/** Shared slider increment for every X-ray image control (contrast, brightness, zoom). */
export const XRAY_CONTROL_STEP = 0.1;

/**
 * Presentation state for the hip-spine X-ray image controls.
 *
 * Brightness and contrast are global (one set applies to every posture) and
 * reset on reload because this store is re-created per session. Zoom is a
 * fit-relative view over the live camera zoom, so mouse-wheel zoom and the
 * slider are two views of the same value.
 */
export const useHipSpineXRayImageControlsViewModel = defineStore(
    'hip-spine-xray-image-controls',
    () => {
        const store = useHipSpineStore();
        const sceneVm = useHipSpineXRaySceneViewModel();

        // Brightness/contrast intent is projected onto the scene state so the
        // Three.js scene can read it via `context.state` without importing this
        // store (docs/threejs-practices.md). The scene state is owned by the
        // scene ViewModel, so the values persist across posture tab switches and
        // reset on reload (the store is re-created per session).
        const adjustments = sceneVm.sceneState.imageAdjustments;
        const brightness = computed<number>({
            get: () => adjustments.brightness,
            set: (value) => {
                adjustments.brightness = value;
            },
        });
        const contrast = computed<number>({
            get: () => adjustments.contrast,
            set: (value) => {
                adjustments.contrast = value;
            },
        });

        // The controls are disabled until a posture's radiograph is loaded. The
        // View binds to this so it never reaches into the Model store itself
        // (docs/mvvm-migration.md: no direct store access from Views).
        const isControlsDisabled = computed(() => !store.activePostureId || store.isLoading);

        // Stable reactive subtree from the scene ViewModel — the camera object
        // itself is never swapped, so a plain alias suffices; reactivity comes
        // from reading `camera.zoom` inside the computeds/watcher below.
        const camera = sceneVm.sceneState.rendering.camera;

        // The full-image fit zoom is the slider's 1.0x baseline. Until the image
        // and viewport sizes are known, fall back to the current camera zoom so the
        // slider reads 1.0x rather than dividing by an unknown.
        const fitZoom = computed(() => {
            const imageSize = store.activePostureId
                ? store.viewState(store.activePostureId)?.imageSize
                : null;
            const viewportSize = sceneVm.sceneState.rendering.viewportSize;
            if (!imageSize || !viewportSize) return camera.zoom || 1;
            return fullViewCameraParams(imageSize, viewportSize).zoom;
        });

        const zoom = computed<number>({
            get: () => clamp(camera.zoom / fitZoom.value, ZOOM_MIN, ZOOM_MAX),
            set: (value) => {
                camera.zoom = clamp(value, ZOOM_MIN, ZOOM_MAX) * fitZoom.value;
            },
        });

        // Keep mouse-wheel zoom inside the slider band so scroll and slider agree at
        // the extremes. Idempotent: only writes when the value is out of band.
        watch(
            () => camera.zoom,
            (current) => {
                const bounded = clamp(current, ZOOM_MIN * fitZoom.value, ZOOM_MAX * fitZoom.value);
                if (bounded !== current) camera.zoom = bounded;
            },
        );

        return { brightness, contrast, zoom, isControlsDisabled };
    },
);
